import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createI18nLoader, ViteI18nLoader, type I18nLoaderConfig, type I18nResourceLoader } from './i18n-loader';
import type { Localized, LocalizedOrNode, LocalizedType } from './types';
import { debug } from './debug';

/**
 * 国际化上下文类型 - 优化版
 */
export interface I18nContextType<Keys extends string = string> {
    /** 当前语言 */
    locale: string;
    /** 切换语言 */
    setLocale: (locale: string) => Promise<void>;
    /** 翻译函数 */
    t: (key: LocalizedType<Keys>, params?: Record<string, unknown>) => string;
    /** 带默认值的翻译函数 */
    td: (key: LocalizedType<Keys>, defaultValue: string, params?: Record<string, unknown>) => string;
    /** 是否准备就绪 */
    isReady: boolean;
    /** 可用的语言列表 */
    availableLocales: string[];
    /** 所有可用的翻译键 */
    availableKeys: string[];
    /** 检查键是否存在 */
    hasKey: (key: string) => boolean;
    /** 获取命名空间资源 */
    getNamespaceResources: (namespace: string) => Record<string, unknown> | undefined;
    /** 是否使用虚拟模块 */
    isUsingVirtualModule: boolean;
}

export interface I18nInterpolation {
    /** 插值前缀，默认为 '{' */
    prefix?: string;
    /** 插值后缀，默认为 '}' */
    suffix?: string;
    /** 转义函数 */
    escape?: (value: unknown) => string;
}

export type I18nPersistenceStorage = 'localStorage' | 'sessionStorage';
export interface I18nPersistence {
    /** 是否启用持久化 */
    enabled?: boolean;
    /** 存储键名 */
    key?: string;
    /** 存储类型 */
    storage?: I18nPersistenceStorage;
}

/**
 * 增强的国际化客户端配置 - 对标 vite-plugin-i18n
 */
export interface I18nClientConfig extends I18nLoaderConfig {
    /** 默认语言 */
    defaultLocale?: string;
    /** 支持的语言列表 */
    supportedLocales?: string[];
    /** 静态资源（用于服务端渲染或初始化） */
    resources?: Record<string, Record<string, unknown>>;
    /** 命名空间配置 */
    namespace?: string;
    /** 资源键前缀 */
    keyPrefix?: string;
    /** 回退到默认语言 */
    fallbackToDefault?: boolean;
    /** 插值选项 */
    interpolation?: I18nInterpolation;
    /** 是否自动检测浏览器语言 */
    detectBrowserLanguage?: boolean;
    /** 持久化设置 */
    persistence?: I18nPersistence;
}

/**
 * 内部使用的完整配置类型
 */
type RequiredI18nClientConfig = I18nClientConfig & {
    defaultLocale: string;
};

/**
 * 增强的国际化客户端类
 */
export class I18nClient {
    public config: RequiredI18nClientConfig;
    private loader: I18nResourceLoader;

    constructor(config: I18nClientConfig) {
        this.config = {
            defaultLocale: 'zh', // 提供默认值
            fallbackToDefault: true,
            detectBrowserLanguage: false,
            interpolation: {
                prefix: '{',
                suffix: '}',
                escape: (value: unknown) => String(value)
            },
            persistence: {
                enabled: true,  // 修复：与createI18nClient保持一致，默认启用持久化
                key: 'user_locale',
                storage: 'localStorage'
            },
            ...config
        } as RequiredI18nClientConfig;

        // 创建资源加载器
        this.loader = createI18nLoader(this.config);
    }

    /** 获取资源加载器 */
    getLoader(): I18nResourceLoader {
        return this.loader;
    }

    /** 获取资源加载函数 */
    getLoadResources() {
        return this.loader.loadResources.bind(this.loader);
    }

    /** 预加载资源 */
    async preloadResources(locales?: string[]) {
        const targetLocales = locales || this.config.supportedLocales || [this.config.defaultLocale];
        return this.loader.preloadResources(targetLocales);
    }

    /** 清除缓存 */
    clearCache(locale?: string) {
        this.loader.clearCache(locale);
    }

    /** 获取所有可用的键 */
    getAvailableKeys(): string[] {
        return this.loader.getAvailableKeys?.() || [];
    }

    /** 检查键是否存在 */
    hasKey(key: string): boolean {
        return this.loader.hasKey?.(key) || false;
    }

    /** 获取命名空间资源 */
    getNamespaceResources(locale: string, namespace: string): Record<string, unknown> | undefined {
        return this.loader.getNamespaceResources?.(locale, namespace);
    }

    /** 检查是否使用虚拟模块 */
    isUsingVirtualModule(): boolean {
        return (this.loader as ViteI18nLoader).isUsingVirtualModule?.() || false;
    }
}

// 默认 persistence 配置
const DEFAULT_PERSISTENCE = {
    enabled: true,
    key: 'user_locale',
    storage: 'localStorage' as const
};

/**
 * 创建国际化客户端 - 支持从虚拟模块自动读取配置或直接传入配置
 */
export function createI18nClient(config?: Partial<I18nClientConfig>): I18nClient {
    // 尝试从虚拟模块获取配置（作为默认值）
    let virtualConfig: Partial<I18nClientConfig> = {};

    try {
        // 只有在没有直接传入资源时，才尝试从虚拟模块读取
        if (!config?.resources) {
            const tempLoader = createI18nLoader();
            const moduleConfig = (tempLoader as ViteI18nLoader).getVirtualModuleConfig?.();
            const moduleResources = (tempLoader as ViteI18nLoader).getVirtualModuleResources?.();

            if (moduleConfig) {
                virtualConfig = {
                    supportedLocales: moduleConfig.supportedLocales,
                    basePath: moduleConfig.basePath,
                    localePattern: moduleConfig.localePattern
                };

                // 如果虚拟模块有资源，也使用它
                if (moduleResources) {
                    virtualConfig.resources = moduleResources;
                }

                if (config?.debug) {
                    // Using configuration from virtual module
                }
            }
        }
    } catch (error) {
        // 虚拟模块配置读取失败时不影响正常使用
        if (config?.debug) {
            // Failed to read virtual module config
        }
    }

    // 合并 persistence 配置（用户配置覆盖默认配置的对应字段）
    const mergedPersistence = {
        ...DEFAULT_PERSISTENCE,
        ...(config?.persistence || {})
    };

    // 合并配置：默认值 < 虚拟模块配置 < 用户传入配置
    const { persistence: _, ...restUserConfig } = config || {};

    const finalConfig: I18nClientConfig = {
        defaultLocale: 'zh', // 提供默认值
        fallbackToDefault: true,
        detectBrowserLanguage: false,
        interpolation: {
            prefix: '{',
            suffix: '}',
            escape: (value: unknown) => String(value)
        },
        ...virtualConfig, // 虚拟模块配置
        ...restUserConfig, // 用户配置优先级最高（排除persistence）
        persistence: mergedPersistence // 使用合并后的 persistence 配置
    };

    // 如果最终配置中没有资源，尝试从用户传入或虚拟模块获取
    if (!finalConfig.resources && !finalConfig.supportedLocales) {
        // 如果用户传入了支持的语言列表，使用它
        if (config?.supportedLocales) {
            finalConfig.supportedLocales = config.supportedLocales;
        }
    }

    return new I18nClient(finalConfig);
}

/**
 * 创建国际化上下文
 */
const I18nContext = createContext<I18nContextType>({
    locale: 'zh',  // 修复：与默认配置保持一致
    setLocale: async () => { },
    t: (_key) => _key,
    td: (_key, defaultValue) => defaultValue,
    isReady: false,
    availableLocales: ['zh', 'en'],  // 修复：添加支持的语言
    availableKeys: [],
    hasKey: () => false,
    getNamespaceResources: () => undefined,
    isUsingVirtualModule: false
});

/**
 * 内部存储资源
 */
const resourcesCache: Record<string, Record<string, unknown>> = {};

/**
 * 解析嵌套键的纯函数 - 优化：移到组件外部避免重复创建
 */
function resolveNestedKeyPure(obj: Record<string, unknown>, path: string): unknown {
    if (typeof path !== 'string') {
        return '';
    }

    const keys = path.split('.');
    let current = obj;

    for (const key of keys) {
        if (current === undefined || current === null) return path;
        current = (current as any)[key];
    }

    return current !== undefined ? current : path;
}

/**
 * 插值处理的纯函数 - 优化：移到组件外部避免重复创建
 */
function interpolateWithConfig(
    text: string,
    params: Record<string, unknown>,
    config: { prefix?: string; suffix?: string; escape?: (value: unknown) => string }
): string {
    const { prefix = '{', suffix = '}', escape = (v: unknown) => String(v) } = config;
    const pattern = new RegExp(`\\${prefix}([\\w.]+)\\${suffix}`, 'g');

    return text.replace(pattern, (_: string, key: string) => {
        const value = resolveNestedKeyPure(params, key);
        return value !== key ? escape(value) : `${prefix}${key}${suffix}`;
    });
}

/**
 * 国际化提供者属性
 */
export interface I18nProviderProps {
    children: React.ReactNode;
    /** 客户端配置或客户端实例 */
    client?: I18nClient | I18nClientConfig;
    /** 传统配置属性（用于向后兼容） */
    defaultLocale?: string;
    locales?: string[];
    loadResources?: (locale: string) => Promise<Record<string, unknown>>;
    resources?: Record<string, Record<string, unknown>>;
}

/**
 * 国际化提供者组件 - 优化版
 */
export function I18nProvider({
    children,
    client,
    defaultLocale = 'zh',  // 修复：与配置保持一致
    locales = ['zh', 'en'], // 修复：支持中英文
    loadResources,
    resources = {}
}: I18nProviderProps) {
    // 处理客户端配置
    const i18nClient = useMemo(() => {
        if (client instanceof I18nClient) {
            return client;
        } else if (client) {
            return new I18nClient(client);
        } else {
            // 向后兼容的配置方式
            return new I18nClient({
                defaultLocale,
                supportedLocales: locales,
                resources,
                fetchResources: loadResources
            });
        }
    }, [client, defaultLocale, locales, loadResources, resources]);

    const loader = i18nClient.getLoader();
    const finalDefaultLocale = i18nClient.config.defaultLocale;
    const finalLocales = useMemo(() => i18nClient.config.supportedLocales || [finalDefaultLocale], [i18nClient.config.supportedLocales, finalDefaultLocale]);

    const [locale, setLocaleState] = useState(finalDefaultLocale);
    const [isReady, setIsReady] = useState(false);

    // 获取可用的键 - 优化：仅在客户端配置变化时重新计算
    const availableKeys = useMemo(() => {
        return i18nClient.getAvailableKeys();
    }, [i18nClient.config]);

    // 初始化时合并静态资源到缓存并设置初始状态
    useEffect(() => {
        let hasInitialResources = false;

        debug.i18n('🚀 I18nProvider: 开始初始化', {
            defaultLocale: finalDefaultLocale,
            availableLocales: finalLocales,
            hasStaticResources: !!i18nClient.config.resources,
            isUsingVirtualModule: i18nClient.isUsingVirtualModule()
        });

        // 合并静态资源到缓存
        if (i18nClient.config.resources) {
            Object.entries(i18nClient.config.resources).forEach(([lang, langResources]) => {
                if (!resourcesCache[lang]) {
                    resourcesCache[lang] = {};
                }
                resourcesCache[lang] = { ...resourcesCache[lang], ...langResources };
                debug.i18n('💿 I18nProvider: 缓存静态资源', {
                    locale: lang,
                    resourceCount: Object.keys(langResources).length
                });

                if (lang === finalDefaultLocale) {
                    hasInitialResources = true;
                }
            });
        }

        // 修复：立即尝试从虚拟模块加载资源，而不是等待异步
        if (!hasInitialResources && i18nClient.isUsingVirtualModule()) {
            const loader = i18nClient.getLoader();

            // 同步检查虚拟模块是否已经缓存了资源
            const virtualResources = (loader as ViteI18nLoader).getVirtualModuleResources?.();
            if (virtualResources) {
                Object.entries(virtualResources).forEach(([lang, langResources]) => {
                    if (!resourcesCache[lang]) {
                        resourcesCache[lang] = langResources as Record<string, unknown>;
                    }
                    if (lang === finalDefaultLocale) {
                        hasInitialResources = true;
                    }
                });

                debug.success('✅ I18nProvider: 从虚拟模块缓存加载资源成功');
            } else {
                // 异步加载虚拟模块资源
                debug.i18n('📦 I18nProvider: 异步加载虚拟模块资源', { locale: finalDefaultLocale });
                loader.loadResources(finalDefaultLocale)
                    .then(resources => {
                        if (resources && Object.keys(resources).length > 0) {
                            resourcesCache[finalDefaultLocale] = resources;
                            setIsReady(true);
                            debug.success('✅ I18nProvider: 虚拟模块资源加载成功', {
                                locale: finalDefaultLocale,
                                resourceCount: Object.keys(resources).length
                            });
                        }
                    })
                    .catch((error) => {
                        debug.error('❌ I18nProvider: 虚拟模块资源加载失败:', error);
                        setIsReady(true); // 即使失败也要设置为就绪
                    });
            }
        }

        // 如果有初始资源或不使用虚拟模块，直接设置为就绪
        if (hasInitialResources || !i18nClient.isUsingVirtualModule()) {
            setIsReady(true);
            debug.success('✅ I18nProvider: 初始化完成', { hasInitialResources });
        }
    }, [finalDefaultLocale, finalLocales, i18nClient, setIsReady]);

    // 设置语言并加载资源
    const setLocale = useCallback(async (newLocale: string) => {
        debug.i18n('🚀 I18nProvider: 开始切换语言', {
            from: locale,
            to: newLocale,
            finalLocales,
            isValid: finalLocales.includes(newLocale)
        });

        if (!finalLocales.includes(newLocale)) {
            debug.error('❌ I18nProvider: 语言不在支持列表中', {
                newLocale,
                finalLocales
            });
            return;
        }

        setIsReady(false);

        try {
            // 加载资源
            if (!resourcesCache[newLocale]) {
                debug.i18n('📦 I18nProvider: 加载资源', { locale: newLocale });
                const newResources = await loader.loadResources(newLocale);
                resourcesCache[newLocale] = newResources;
                debug.success('✅ I18nProvider: 资源加载成功', {
                    locale: newLocale,
                    resourceKeys: Object.keys(newResources).length
                });
            } else {
                debug.i18n('💿 I18nProvider: 使用缓存资源', { locale: newLocale });
            }

            setLocaleState(newLocale);
            debug.success('✅ I18nProvider: 语言切换成功', { newLocale });

            // 更新 HTML lang 属性
            if (typeof document !== 'undefined') {
                document.documentElement.lang = newLocale;
                debug.i18n('🌍 I18nProvider: 更新 HTML lang 属性', { lang: newLocale });
            }

            // 持久化用户语言选择
            if (i18nClient.config.persistence?.enabled && typeof window !== 'undefined') {
                try {
                    const storage = i18nClient.config.persistence.storage === 'sessionStorage'
                        ? sessionStorage
                        : localStorage;
                    const key = i18nClient.config.persistence.key || 'user_locale';
                    storage.setItem(key, newLocale);
                    debug.i18n('💾 I18nProvider: 保存语言偏好', { key, locale: newLocale });
                } catch (error) {
                    debug.error('❌ I18nProvider: 保存语言偏好失败:', error);
                }
            }
        } catch (error) {
            debug.error('❌ I18nProvider: 加载资源失败', {
                locale: newLocale,
                error: error instanceof Error ? error.message : error
            });
            // 在失败时不要更改locale状态
        } finally {
            setIsReady(true);
        }
    }, [finalLocales, loader, i18nClient.config.persistence, locale]);


    // 插入参数 'Hello, {name}!' + { name: 'World' } => 'Hello, World!' - 优化：稳定化插值配置
    const interpolationConfig = useMemo(() => i18nClient.config.interpolation || {}, [i18nClient.config.interpolation]);

    const interpolate = useCallback((text: string, params?: Record<string, unknown>): string => {
        if (!params) return text;
        return interpolateWithConfig(text, params, interpolationConfig);
    }, [interpolationConfig]);

    // 增强的翻译函数 - 优化：减少依赖项，稳定化配置
    const clientConfig = useMemo(() => ({
        keyPrefix: i18nClient.config.keyPrefix,
        fallbackToDefault: i18nClient.config.fallbackToDefault
    }), [i18nClient.config.keyPrefix, i18nClient.config.fallbackToDefault]);

    const t = useCallback((key: string, params?: Record<string, unknown>): string => {
        if (typeof key !== 'string' || !key) {
            return '';
        }

        const fullKey = clientConfig.keyPrefix ? `${clientConfig.keyPrefix}.${key}` : key;
        const currentResources = resourcesCache[locale] || {};
        let value = resolveNestedKeyPure(currentResources, fullKey);

        // 如果找不到翻译且启用了回退，尝试从默认语言获取
        if (value === fullKey &&
            clientConfig.fallbackToDefault &&
            locale !== finalDefaultLocale) {

            const defaultResources = resourcesCache[finalDefaultLocale] || {};
            const defaultValue = resolveNestedKeyPure(defaultResources, fullKey);

            if (defaultValue !== fullKey) {
                value = defaultValue;
            }
        }

        // 如果仍然找不到，返回原始键名（不包含前缀）
        if (value === fullKey) {
            value = key;
        }

        return interpolate(String(value), params);
    }, [locale, finalDefaultLocale, clientConfig, interpolate]);

    // 带默认值的翻译函数 - 优化：复用clientConfig，减少重复逻辑
    const td = useCallback((key: string, defaultValue: string, params?: Record<string, unknown>): string => {
        if (typeof key !== 'string' || !key) {
            return defaultValue;
        }

        const fullKey = clientConfig.keyPrefix ? `${clientConfig.keyPrefix}.${key}` : key;
        const currentResources = resourcesCache[locale] || {};
        let value = resolveNestedKeyPure(currentResources, fullKey);

        // 如果找不到翻译且启用了回退，尝试从默认语言获取
        if (value === fullKey &&
            clientConfig.fallbackToDefault &&
            locale !== finalDefaultLocale) {

            const defaultResources = resourcesCache[finalDefaultLocale] || {};
            const defaultTranslation = resolveNestedKeyPure(defaultResources, fullKey);

            if (defaultTranslation !== fullKey) {
                value = defaultTranslation;
            }
        }

        // 如果仍然找不到翻译，返回提供的默认值
        if (value === fullKey) {
            value = defaultValue;
        }

        return interpolate(String(value), params);
    }, [locale, finalDefaultLocale, clientConfig, interpolate]);

    // 检查键是否存在
    const hasKey = useCallback((key: string): boolean => {
        return i18nClient.hasKey(key);
    }, [i18nClient]);

    // 获取命名空间资源
    const getNamespaceResources = useCallback((namespace: string): Record<string, unknown> | undefined => {
        return i18nClient.getNamespaceResources(locale, namespace);
    }, [i18nClient, locale]);

    // 组件挂载时恢复用户语言选择和浏览器语言检测
    useEffect(() => {
        let targetLocale = finalDefaultLocale; // 始终从默认语言开始
        let shouldChangeLocale = false;

        debug.i18n('🔍 I18nProvider: 检测初始语言', {
            defaultLocale: finalDefaultLocale,
            currentLocale: locale,
            persistenceEnabled: i18nClient.config.persistence?.enabled,
            detectBrowserLanguage: i18nClient.config.detectBrowserLanguage
        });

        try {
            // 只有在明确启用持久化时才尝试恢复
            if (i18nClient.config.persistence?.enabled && typeof window !== 'undefined') {
                const storage = i18nClient.config.persistence.storage === 'sessionStorage'
                    ? sessionStorage
                    : localStorage;
                const key = i18nClient.config.persistence.key || 'user_locale';
                const savedLocale = storage.getItem(key);

                debug.i18n('💾 I18nProvider: 检查保存的语言', {
                    savedLocale,
                    isValid: savedLocale && finalLocales.includes(savedLocale)
                });

                if (savedLocale && finalLocales.includes(savedLocale)) {
                    targetLocale = savedLocale;
                    shouldChangeLocale = true;
                    debug.success('✅ I18nProvider: 恢复保存的语言', { locale: savedLocale });
                }
            }

            // 只有在没有保存的语言且明确启用浏览器检测时才使用浏览器语言
            if (!shouldChangeLocale &&
                i18nClient.config.detectBrowserLanguage &&
                typeof navigator !== 'undefined') {
                const browserLocale = navigator.language.split('-')[0];

                debug.i18n('🌍 I18nProvider: 检测浏览器语言', {
                    browserLocale,
                    isSupported: finalLocales.includes(browserLocale),
                    isDifferent: browserLocale !== finalDefaultLocale
                });

                if (finalLocales.includes(browserLocale) && browserLocale !== finalDefaultLocale) {
                    targetLocale = browserLocale;
                    shouldChangeLocale = true;
                    debug.success('✅ I18nProvider: 使用浏览器语言', { locale: browserLocale });
                }
            }
        } catch (error) {
            debug.error('❌ I18nProvider: 语言检测失败:', error);
        }

        // 修复：无论是否需要改变语言，都要确保资源被加载
        // 这是关键修复，确保页面刷新后资源能正确加载
        if (targetLocale !== locale || !resourcesCache[targetLocale]) {
            debug.i18n('🚀 I18nProvider: 设置初始语言', { targetLocale, currentLocale: locale });
            setLocale(targetLocale);
        } else {
            // 即使语言没有改变，也要确保界面处于就绪状态
            if (!isReady) {
                debug.i18n('✅ I18nProvider: 语言无需改变，设置为就绪状态');
                setIsReady(true);
            }
        }
    }, [finalDefaultLocale, finalLocales, i18nClient, locale, setLocale, isReady, setIsReady]); // 添加必要的依赖项

    return (
        <I18nContext.Provider
            value={{
                locale,
                setLocale,
                t,
                td,
                isReady,
                availableLocales: finalLocales,
                availableKeys,
                hasKey,
                getNamespaceResources,
                isUsingVirtualModule: i18nClient.isUsingVirtualModule()
            }}
        >
            {children}
        </I18nContext.Provider>
    );
}

/**
 * 使用国际化上下文的 Hook
 */
export function useI18n<Keys extends string = string>(): I18nContextType<Keys> {
    const context = useContext(I18nContext) as I18nContextType<Keys>;
    if (!context) {
        throw new Error('useI18n must be used within an I18nProvider');
    }
    return context;
}

/**
 * 用于翻译本地化对象的 Hook - 优化版
 */
export function useTranslateLocalized<Keys extends string = string>(): (localized: LocalizedOrNode<Keys> | undefined) => React.ReactNode {
    const { t } = useI18n<Keys>();
    return useCallback((localized: Localized<Keys> | React.ReactNode | undefined) => {
        return translateLocalized(localized, t);
    }, [t]);
}

/**
 * 转换本地化对象为字符串 - 优化版
 */
export function translateLocalized<Keys extends string = string>(
    localized: LocalizedOrNode<Keys> | undefined,
    translate: (key: string) => string
): string | React.ReactNode {
    if (!localized) return '';

    // 如果是字符串，直接翻译
    if (typeof localized === 'string') {
        return translate(localized);
    }

    // 如果是 Localized 对象
    if (localized && typeof localized === 'object' && 'localizedId' in localized) {
        const localizedId = (localized as Localized<Keys>).localizedId;
        // 确保 localizedId 是字符串
        if (typeof localizedId === 'string') {
            return translate(localizedId);
        } else {
            // Invalid localizedId type
            return '';
        }
    }

    // 其他情况返回原值
    return localized;
}

/**
 * 创建本地化对象
 */
export function localize<Keys extends string = string>(key: LocalizedType<Keys>): Localized<Keys> {
    return { localizedId: key };
}

/**
 * 使用国际化键验证 Hook
 */
export function useI18nKeyValidator<Keys extends string = string>() {
    const { hasKey, availableKeys } = useI18n<Keys>();

    return {
        /** 检查键是否存在 */
        hasKey,
        /** 获取所有可用键 */
        getAvailableKeys: () => availableKeys,
        /** 验证键并返回建议 */
        validateKey: (key: string) => {
            if (hasKey(key)) {
                return { valid: true, suggestions: [] };
            }

            // 提供相似键的建议
            const suggestions = availableKeys
                .filter(availableKey =>
                    availableKey.toLowerCase().includes(key.toLowerCase()) ||
                    key.toLowerCase().includes(availableKey.toLowerCase())
                )
                .slice(0, 5);

            return { valid: false, suggestions };
        }
    };
}

/**
 * 使用命名空间 Hook
 */
export function useI18nNamespace<Keys extends string = string>(namespace: string) {
    const { getNamespaceResources, t, locale } = useI18n<Keys>();

    const namespaceResources = useMemo(() => {
        return getNamespaceResources(namespace);
    }, [getNamespaceResources, namespace]);

    const tNamespace = useCallback((key: string, params?: Record<string, unknown>) => {
        const namespacedKey = `${namespace}.${key}`;
        return t(namespacedKey, params);
    }, [t, namespace]);

    return {
        /** 命名空间内的翻译函数 */
        t: tNamespace,
        /** 获取命名空间资源 */
        resources: namespaceResources,
        /** 当前语言 */
        locale,
        /** 检查命名空间是否存在 */
        exists: !!namespaceResources
    };
}

/**
 * 使用复数形式 Hook
 */
export function usePlural<Keys extends string = string>() {
    const { t } = useI18n<Keys>();

    return useCallback((key: string, count: number, params?: Record<string, unknown>) => {
        const pluralKey = count === 1 ? `${key}.one` : `${key}.other`;
        return t(pluralKey, { count, ...params });
    }, [t]);
}

/**
 * 导出重构后的主要接口
 */
export {
    createI18nLoader,
    ViteI18nLoader,
    type I18nLoaderConfig,
    type I18nResourceLoader
} from './i18n-loader';

/**
 * 解析包含组件标记的文本
 * 支持 {Component}内容{/Component} 格式
 */
export function parseComponentText(text: string): React.ReactNode {
    const parts: React.ReactNode[] = [];
    const regex = /{(\w+)}(.*?){\/\1}/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
        // 添加组件前的文本
        if (match.index > lastIndex) {
            parts.push(text.substring(lastIndex, match.index));
        }

        // 根据组件名渲染
        const componentName = match[1];
        const componentContent = match[2];

        switch (componentName) {
            case 'strong':
            case 'b':
                parts.push(<strong key={match.index}>{componentContent}</strong>);
                break;
            case 'em':
            case 'i':
                parts.push(<em key={match.index}>{componentContent}</em>);
                break;
            case 'code':
                parts.push(<code key={match.index}>{componentContent}</code>);
                break;
            case 'mark':
                parts.push(<mark key={match.index}>{componentContent}</mark>);
                break;
            case 'small':
                parts.push(<small key={match.index}>{componentContent}</small>);
                break;
            default:
                // 未知组件，保持原样
                parts.push(match[0]);
        }

        lastIndex = match.index + match[0].length;
    }

    // 添加剩余文本
    if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
    }

    return parts.length === 1 ? parts[0] : <>{parts}</>;
}

/**
 * 使用 I18nMessage 的调试 Hook
 */
export function useI18nMessageDebug<Keys extends string = string>() {
    const { isReady, locale, availableKeys, isUsingVirtualModule } = useI18n<Keys>();

    return {
        /** 当前状态 */
        isReady,
        locale,
        /** 调试信息 */
        debugInfo: {
            totalKeys: availableKeys.length,
            isUsingVirtualModule,
            hasKeys: availableKeys.length > 0
        },
        /** 验证翻译键 */
        validateKey: (key: string) => {
            return {
                exists: availableKeys.includes(key),
                ready: isReady,
                locale
            };
        }
    };
}

/**
 * I18nMessage 性能优化 Hook
 */
export function useI18nMessageMemo<Keys extends string = string>(
    id: LocalizedType<Keys>,
    params?: Record<string, any>,
    defaultValue?: string
) {
    const { t, isReady } = useI18n<Keys>();

    return useMemo(() => {
        if (!isReady) {
            return defaultValue || "";
        }

        const translated = t(id as string, params);
        return translated === id && defaultValue ? defaultValue : translated;
    }, [id, params, defaultValue, t, isReady]);
}
